import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";

const empty = {
  employee_id: "", name: "", date_of_joining: "", department: "", phone: "",
  bank_account: "", ifsc: "", fixed_salary_per_day: 0,
  pf_enabled: false, esi_enabled: true, active: true,
};

export default function Employees() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/employees");
    setRows(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const openEdit = (e) => {
    setForm({
      employee_id: e.employee_id, name: e.name,
      date_of_joining: e.date_of_joining || "", department: e.department || "",
      phone: e.phone || "", bank_account: e.bank_account || "", ifsc: e.ifsc || "",
      fixed_salary_per_day: e.fixed_salary_per_day, pf_enabled: e.pf_enabled,
      esi_enabled: e.esi_enabled, active: e.active,
    });
    setEditingId(e.id); setOpen(true);
  };

  const save = async () => {
    try {
      if (editingId) await api.put(`/employees/${editingId}`, { ...form, fixed_salary_per_day: Number(form.fixed_salary_per_day) });
      else await api.post("/employees", { ...form, fixed_salary_per_day: Number(form.fixed_salary_per_day) });
      toast.success(editingId ? "Employee updated" : "Employee added");
      setOpen(false); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this employee and all attendance data?")) return;
    await api.delete(`/employees/${id}`);
    toast.success("Employee deleted");
    load();
  };

  return (
    <div className="p-8 space-y-6" data-testid="employees-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Employees</h1>
          <p className="text-sm text-slate-500 mt-1">Manage employee master data.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openAdd} data-testid="add-employee-btn"><Plus size={16} className="mr-1" /> Add Employee</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editingId ? "Edit Employee" : "New Employee"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <Field label="Employee ID"><Input value={form.employee_id} onChange={(e)=>setForm({...form, employee_id:e.target.value})} data-testid="emp-id-input" /></Field>
              <Field label="Name"><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="emp-name-input" /></Field>
              <Field label="Date of Joining"><Input type="date" value={form.date_of_joining || ""} onChange={(e)=>setForm({...form, date_of_joining:e.target.value})} /></Field>
              <Field label="Department"><Input value={form.department} onChange={(e)=>setForm({...form, department:e.target.value})} /></Field>
              <Field label="Phone"><Input value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} /></Field>
              <Field label="Fixed Salary / Day"><Input type="number" step="0.01" value={form.fixed_salary_per_day} onChange={(e)=>setForm({...form, fixed_salary_per_day:e.target.value})} data-testid="emp-fixed-input" /></Field>
              <Field label="Bank Account"><Input value={form.bank_account} onChange={(e)=>setForm({...form, bank_account:e.target.value})} /></Field>
              <Field label="IFSC"><Input value={form.ifsc} onChange={(e)=>setForm({...form, ifsc:e.target.value})} /></Field>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={form.pf_enabled} onCheckedChange={(v)=>setForm({...form, pf_enabled:v})} data-testid="emp-pf-switch" />
                <Label>PF Applicable</Label>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={form.esi_enabled} onCheckedChange={(v)=>setForm({...form, esi_enabled:v})} data-testid="emp-esi-switch" />
                <Label>ESI Applicable</Label>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Switch checked={form.active} onCheckedChange={(v)=>setForm({...form, active:v})} />
                <Label>Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="emp-save-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-slate-100">
            <tr>
              {["Emp ID","Name","Department","Fixed/Day","PF","ESI","Status","Actions"].map((h)=>(
                <th key={h} className="text-left px-4 py-2 text-xs uppercase tracking-wider font-mono">{h==="Actions"?"":h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length===0 && <tr><td colSpan={8} className="text-center text-slate-400 py-10">No employees yet. Click "Add Employee".</td></tr>}
            {rows.map((r)=>(
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`emp-row-${r.employee_id}`}>
                <td className="px-4 py-2 font-mono">{r.employee_id}</td>
                <td className="px-4 py-2 font-medium">{r.name}</td>
                <td className="px-4 py-2 text-slate-600">{r.department || "-"}</td>
                <td className="px-4 py-2 num text-right">{Number(r.fixed_salary_per_day).toFixed(2)}</td>
                <td className="px-4 py-2"><Pill on={r.pf_enabled} color="violet" /></td>
                <td className="px-4 py-2"><Pill on={r.esi_enabled} color="sky" /></td>
                <td className="px-4 py-2"><Pill on={r.active} color="emerald" label={r.active?"Active":"Inactive"} /></td>
                <td className="px-4 py-2 text-right">
                  <button onClick={()=>openEdit(r)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`emp-edit-${r.employee_id}`}><Pencil size={14}/></button>
                  <button onClick={()=>remove(r.id)} className="p-1.5 rounded hover:bg-rose-100 text-rose-600 ml-1" data-testid={`emp-del-${r.employee_id}`}><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-slate-600 uppercase tracking-wider font-mono">{label}</Label>
      {children}
    </div>
  );
}

function Pill({ on, color, label }) {
  const c = on ? {
    violet:"bg-violet-100 text-violet-800 border-violet-200",
    sky:"bg-sky-100 text-sky-800 border-sky-200",
    emerald:"bg-emerald-100 text-emerald-800 border-emerald-200",
  }[color] : "bg-slate-100 text-slate-500 border-slate-200";
  return <span className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full border ${c}`}>{label || (on ? "Yes" : "No")}</span>;
}
